import { invalidateDeviceStateSchema } from '@/lib/queryClient'
import { type DeviceStatusExpression, isDeviceStatusExpression } from '@/types/deviceStatus'
import { apiCall, tenantScopedConfig } from './axiosInstance'
import { type DerivedDeviceState, derivedDeviceStateApi } from './derivedDeviceStateApi'

/**
 * A device_state column is either stream-backed — a `/v3/state-keys` alias of a stream field — or
 * derived, which is a `/derived-device-state` row projected into the same list so the device_state
 * fields table can present both as columns. The `?: never` arms turn a value carrying both kinds
 * into a compile error.
 */
export interface StreamBackedStateKey {
	display_name: string
	stream_name: string
	field_name: string
	/** The source column's clickhouse type, e.g. 'Float64'. */
	type: string
	/** The source column's formatter. */
	formatter: string | null
	expression?: never
	derived_id?: never
}

export interface DerivedStateKey {
	display_name: string
	derived_id: number
	expression: DeviceStatusExpression
	stream_name?: never
	field_name?: never
}

export type StateKey = StreamBackedStateKey | DerivedStateKey

export const isDerivedStateKey = (stateKey: StateKey): stateKey is DerivedStateKey =>
	isDeviceStatusExpression(stateKey.expression)

export const isStreamBackedStateKey = (stateKey: StateKey): stateKey is StreamBackedStateKey =>
	typeof stateKey.stream_name === 'string' && typeof stateKey.field_name === 'string'

/** Only stream-backed keys are created here — derived columns go to `/derived-device-state`.
 *  `type`/`formatter` are resolved server-side from the source column, not sent on create. */
export type CreateStateKeyRequest = Omit<StreamBackedStateKey, 'display_name' | 'derived_id' | 'type' | 'formatter'>

export interface StateKeyResponse {
	name: string
	status: string
}

export interface StateKeyStatusResponse {
	status: string
}

export const stateKeysApi = {
	/**
	 * A device_state column is an alias of `{stream_name}.{field_name}`; each stream-backed key carries
	 * its source column's `type`/`formatter` inline, so callers don't need to fetch the source stream.
	 * GET /v3/state-keys
	 */
	getStateKeys: async (tenantId?: string, impersonateAdmin = true): Promise<StateKey[]> =>
		await apiCall('/v3/state-keys', tenantScopedConfig(tenantId, impersonateAdmin)),

	createStateKey: async (
		displayName: string,
		request: CreateStateKeyRequest,
		tenantId?: string,
	): Promise<StateKeyResponse> => {
		const response = await apiCall<StateKeyResponse>(`/v3/state-keys/${encodeURIComponent(displayName)}`, {
			method: 'POST',
			data: request,
			...tenantScopedConfig(tenantId),
		})
		void invalidateDeviceStateSchema()
		return response
	},

	renameStateKey: async (oldName: string, newName: string, tenantId?: string): Promise<StateKeyStatusResponse> => {
		const response = await apiCall<StateKeyStatusResponse>(
			`/v3/state-keys/${encodeURIComponent(oldName)}/rename/${encodeURIComponent(newName)}`,
			{
				method: 'POST',
				...tenantScopedConfig(tenantId),
			},
		)
		void invalidateDeviceStateSchema()
		return response
	},

	deleteStateKey: async (displayName: string, tenantId?: string): Promise<StateKeyStatusResponse> => {
		const response = await apiCall<StateKeyStatusResponse>(`/v3/state-keys/${encodeURIComponent(displayName)}`, {
			method: 'DELETE',
			...tenantScopedConfig(tenantId),
		})
		void invalidateDeviceStateSchema()
		return response
	},
}

const toDerivedStateKey = (derived: DerivedDeviceState): DerivedStateKey => ({
	display_name: derived.name,
	derived_id: derived.id,
	expression: derived.expressions,
})

/**
 * Every device_state column: stream-backed aliases plus the derived states, which live in their own
 * table but present as columns of the same virtual stream.
 */
export async function getStateKeysWithDerived(tenantId?: string, impersonateAdmin = true): Promise<StateKey[]> {
	const [stateKeys, derived] = await Promise.all([
		stateKeysApi.getStateKeys(tenantId, impersonateAdmin),
		derivedDeviceStateApi.getDerivedDeviceStates(tenantId, impersonateAdmin),
	])
	return [...stateKeys, ...derived.map(toDerivedStateKey)]
}

export interface OptionalDerivedStateKeys {
	stateKeys: StateKey[]
	/**
	 * `/derived-device-state` failed, so `stateKeys` carries no derived columns. Callers must not read
	 * that as "there are none" — resolving a saved selection against this list drops every derived
	 * column the panel had.
	 */
	derivedUnavailable: boolean
}

/**
 * As `getStateKeysWithDerived`, but a failing `/derived-device-state` costs only the derived columns
 * rather than the whole list. For surfaces where they are additive — the panel editors — not the
 * Streams page that manages them. The failure is reported, never silently folded into an empty list.
 */
export async function getStateKeysWithOptionalDerived(
	tenantId?: string,
	impersonateAdmin = true,
): Promise<OptionalDerivedStateKeys> {
	const [stateKeys, derived] = await Promise.all([
		stateKeysApi.getStateKeys(tenantId, impersonateAdmin),
		derivedDeviceStateApi
			.getDerivedDeviceStates(tenantId, impersonateAdmin)
			.then(states => ({ states, failed: false }))
			.catch(error => {
				console.error('Failed to load derived device states', error)
				return { states: [] as DerivedDeviceState[], failed: true }
			}),
	])
	return {
		stateKeys: [...stateKeys, ...derived.states.map(toDerivedStateKey)],
		derivedUnavailable: derived.failed,
	}
}

/** Names of the device_state columns backed by `streamName`, which block deleting that stream. */
export const stateKeyNamesForStream = (stateKeys: StateKey[], streamName: string): string[] =>
	stateKeys
		.filter((key): key is StreamBackedStateKey => isStreamBackedStateKey(key) && key.stream_name === streamName)
		.map(key => key.display_name || key.field_name)

export interface DeleteStateKeysResult {
	deleted: string[]
	failed: Array<{ name: string; error: unknown }>
}

/**
 * Delete several device_state columns, tolerating dependency ordering.
 *
 * The backend rejects deleting a key another key references, and references chain
 * (`b = a * 2`, `c = b * 2`), so no safe order exists without parsing expressions. Retries in
 * passes until one makes no progress. Never throws; callers inspect `failed`.
 */
export async function deleteStateKeys(keys: StateKey[], tenantId?: string): Promise<DeleteStateKeysResult> {
	const deleted: string[] = []
	let pending = keys
	let failed: DeleteStateKeysResult['failed'] = []

	while (pending.length > 0) {
		const stillPending: StateKey[] = []
		failed = []
		for (const key of pending) {
			try {
				if (isDerivedStateKey(key)) {
					await derivedDeviceStateApi.deleteDerivedDeviceState(key.derived_id, tenantId)
				} else {
					await stateKeysApi.deleteStateKey(key.display_name, tenantId)
				}
				deleted.push(key.display_name)
			} catch (error) {
				stillPending.push(key)
				failed.push({ name: key.display_name, error })
			}
		}
		if (stillPending.length === pending.length) {
			return { deleted, failed }
		}
		pending = stillPending
	}

	return { deleted, failed: [] }
}
